// Statuspage public summary endpoint — no auth required, CORS-friendly.
export const STATUSPAGE_URL = 'https://greenbow.statuspage.io/api/v2/summary.json'

export interface StatusPageComponent {
  id: string
  name: string
  status: string
  description: string | null
  group_id: string | null
  group: boolean
  only_show_if_degraded: boolean
}

export interface IncidentUpdate {
  id: string
  status: string
  body: string
  created_at: string
  updated_at: string
}

export interface StatusPageIncident {
  id: string
  name: string
  status: string
  impact: string
  created_at: string
  updated_at: string
  incident_updates: IncidentUpdate[]
  components: StatusPageComponent[]
}

export interface StatusPageMaintenance {
  id: string
  name: string
  status: string
  scheduled_for: string
  scheduled_until: string
  created_at: string
  updated_at: string
  incident_updates: IncidentUpdate[]
  components: StatusPageComponent[]
}

export interface StatusPageSummary {
  page: { id: string; name: string; url: string; updated_at: string }
  status: {
    indicator: 'none' | 'minor' | 'major' | 'critical'
    description: string
  }
  components: StatusPageComponent[]
  incidents: StatusPageIncident[]
  scheduled_maintenances: StatusPageMaintenance[]
}

export interface GroupedComponents {
  groups: { group: StatusPageComponent; children: StatusPageComponent[] }[]
  ungrouped: StatusPageComponent[]
}

export function groupComponents(all: StatusPageComponent[]): GroupedComponents {
  const groups = all.filter((c) => c.group)
  const grouped = new Set<string>()
  const groupRows = groups.map((g) => {
    const children = all.filter((c) => c.group_id === g.id)
    children.forEach((c) => grouped.add(c.id))
    return { group: g, children }
  })
  const ungrouped = all.filter((c) => !c.group && !grouped.has(c.id))

  // Fallback: when Statuspage returns no native groups, derive synthetic
  // ones from a shared first-word prefix (e.g. "Zaptec API" + "Zaptec
  // Portal" → "Zaptec" group). Single-prefix components stay ungrouped.
  if (groupRows.length === 0 && ungrouped.length > 0) {
    return groupByNamePrefix(ungrouped)
  }
  return { groups: groupRows, ungrouped }
}

function groupByNamePrefix(components: StatusPageComponent[]): GroupedComponents {
  const byPrefix = new Map<string, StatusPageComponent[]>()
  for (const c of components) {
    const prefix = c.name.split(' ')[0]
    if (!prefix) continue
    const list = byPrefix.get(prefix) ?? []
    list.push(c)
    byPrefix.set(prefix, list)
  }
  const groups: { group: StatusPageComponent; children: StatusPageComponent[] }[] = []
  const ungrouped: StatusPageComponent[] = []
  for (const [prefix, list] of byPrefix) {
    if (list.length < 2) {
      ungrouped.push(...list)
      continue
    }
    const syntheticGroup: StatusPageComponent = {
      id: `synthetic-${prefix}`,
      name: prefix,
      status: 'operational',
      description: null,
      group_id: null,
      group: true,
      only_show_if_degraded: false,
    }
    groups.push({ group: syntheticGroup, children: list })
  }
  return { groups, ungrouped }
}

const STATUS_SEVERITY: Record<string, number> = {
  operational: 0,
  under_maintenance: 1,
  degraded_performance: 2,
  partial_outage: 3,
  major_outage: 4,
}

export function worstStatus(children: { status: string }[]): string {
  if (!children.length) return 'operational'
  return children.reduce((worst, c) => {
    const a = STATUS_SEVERITY[worst] ?? 0
    const b = STATUS_SEVERITY[c.status] ?? 0
    return b > a ? c.status : worst
  }, 'operational')
}
